import { useEffect, useRef, useState } from 'react';
import { Building2, Search } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { accountService } from '@/services/account.service';
import type { SelectedAccountRef } from '@/modules/analytics-v2/system-admin-ui-config';
import { FilterOption, FilterPill } from '@/components/ui/filter-pill';

/** Server-side search waits this long after the last keystroke. */
const SEARCH_DELAY_MS = 250;

interface AccountPickerProps {
  value: SelectedAccountRef | null;
  onChange: (account: SelectedAccountRef) => void;
}

/** Picks the one account a Sentinel Analytics page shows: search by name, or type an account id. */
export function AccountPicker({ value, onChange }: AccountPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<SelectedAccountRef[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DELAY_MS);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    accountService
      .list({ page: 1, pageSize: 100, search: debouncedSearch || undefined })
      // Legacy Mongo-only accounts have no Postgres id, so analytics can't scope to them.
      .then(({ accounts: list }) => !cancelled && setAccounts(list.filter((a) => a.id != null).map((a) => ({ id: String(a.id), name: a.name ?? undefined }))))
      .catch(() => !cancelled && setAccounts([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, debouncedSearch]);

  // A typed id that the search didn't list can still be picked.
  const typedId = /^[1-9]\d*$/.test(search.trim()) ? search.trim() : null;
  const rows = typedId && !accounts.some((a) => a.id === typedId) ? [{ id: typedId }, ...accounts] : accounts;

  const pick = (account: SelectedAccountRef) => {
    onChange(account);
    setOpen(false);
    setSearch('');
  };

  return (
    <FilterPill
      label={value ? (value.name ?? `Account #${value.id}`) : 'Select account'}
      icon={<Building2 aria-hidden='true' className='size-3.5 shrink-0' />}
      contentClassName='w-[280px] gap-2'
      open={open}
      onOpenChange={setOpen}
    >
      <label className='mx-1 flex h-8 shrink-0 items-center gap-2 rounded-lg border border-gray-500 bg-white px-2.5 text-[13px] leading-[18px] text-gray-800 focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-indigo-300'>
        <Search aria-hidden='true' className='size-3.5 shrink-0' />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              listRef.current?.querySelector<HTMLButtonElement>('[role=option]')?.focus();
            }
          }}
          placeholder='Search accounts or type an ID'
          aria-label='Search accounts'
          className='min-w-0 flex-1 bg-transparent text-text-emphasis outline-none placeholder:text-gray-800'
        />
      </label>
      <div ref={listRef} role='listbox' aria-label='Accounts' className='custom-scrollbar flex max-h-[320px] min-h-0 flex-col gap-0.5 overflow-y-auto'>
        {loading && rows.length === 0 ? (
          <p className='px-2.5 py-1.5 text-[13px] leading-[18px] text-gray-800'>Searching…</p>
        ) : rows.length === 0 ? (
          <p className='px-2.5 py-1.5 text-[13px] leading-[18px] text-gray-800'>No accounts found</p>
        ) : (
          rows.map((account) => (
            <FilterOption
              key={account.id}
              label={account.name ?? `Account #${account.id}`}
              hint={`#${account.id}`}
              selected={value?.id === account.id}
              onSelect={() => pick(account)}
            />
          ))
        )}
      </div>
    </FilterPill>
  );
}
